<template>
  <CodSheet
    v-model="isOpen"
    show-header
    :title="t('terminals.register')"
    width="560px"
  >
    <div class="p-4">
      <UAlert
        v-if="!terminalTypes.filter(t => t.enabled).length"
        color="error"
        variant="soft"
        icon="i-lucide-circle-alert"
        class="my-4"
        :description="t('general.not_possible_from_web_app')"
      />

      <div v-else class="flex flex-col gap-4">
        <div
          v-if="terminalTypes.filter(t => t.enabled).length > 1"
          class="grid gap-2"
          :style="{ gridTemplateColumns: `repeat(${terminalTypes.length}, minmax(0, 1fr))` }"
        >
          <button
            v-for="terType in terminalTypes"
            :key="terType.type"
            type="button"
            class="flex h-22.5 cursor-pointer flex-col items-center justify-center gap-2 rounded-btn px-1 py-4 text-identifier"
            :class="selectedType?.type === terType.type ? 'bg-primary text-white' : 'bg-accented text-default hover:bg-elevated'"
            @click="selectedType = terType"
          >
            <UIcon :name="terType.icon" class="size-6 shrink-0" />
            <div class="leading-tight">{{ getTerminalTypeLabel(terType.type) }}</div>
          </button>
        </div>

        <div v-if="selectedType?.error">
          <UAlert
            color="error"
            variant="soft"
            icon="i-lucide-circle-alert"
            class="my-4"
            :description="t(`terminals.${selectedType.error}`)"
          />
        </div>

        <div v-else-if="selectedType?.type === TerminalType.cashRegister" class="flex flex-col gap-4">
          <UAlert
            v-if="!terminalTypes.filter(t => t.enabled).length"
            color="error"
            variant="soft"
            icon="i-lucide-circle-alert"
            class="my-4"
          >
            <template #description>
              {{ t('cash_register.registration_needed') }}
              <br><br>
              {{ t('cash_register.registered_number', { count: cashRegisters.length ? 2 : 1, value: cashRegisters.length } ) }}
            </template>
          </UAlert>

          <UFormField :label="t('general.name')" :error="cashRegisterForm.$errors.name?.[0] || undefined">
            <UInput
              v-model="cashRegisterForm.data.name"
              class="w-full"
              @input="cashRegisterForm.clearErrors('name')"
              @blur="cashRegisterForm.validate($vCashRegisterForm, 'name')"
            />
          </UFormField>

          <UFormField
            v-if="I18n.c('cashRegister.requireResellerHash')"
            :label="t('cash_register.password')"
            :error="cashRegisterForm.$errors.reseller_hash?.[0] || undefined"
          >
            <UInput
              v-model="cashRegisterForm.data.reseller_hash"
              class="w-full"
              :type="showPassword ? 'text' : 'password'"
              @input="cashRegisterForm.clearErrors('reseller_hash')"
              @blur="cashRegisterForm.validate($vCashRegisterForm, 'reseller_hash')"
            >
              <template #trailing>
                <UButton
                  color="neutral"
                  variant="link"
                  size="sm"
                  :icon="showPassword ? 'i-lucide-eye' : 'i-lucide-eye-off'"
                  :aria-label="t('cash_register.password')"
                  @click="showPassword = !showPassword"
                />
              </template>
            </UInput>
          </UFormField>

          <UFormField
            v-if="I18n.c('order.registerMethod') === 'legacy'"
            :label="t('cash_register.activation_code')"
            :error="cashRegisterForm.$errors.activation_code?.[0] || undefined"
          >
            <UInput
              v-model="cashRegisterForm.data.activation_code"
              class="w-full"
              @input="cashRegisterForm.clearErrors('activation_code')"
              @blur="cashRegisterForm.validate($vCashRegisterForm, 'activation_code')"
            />
          </UFormField>

          <template v-if="I18n.c('cashRegister.allowLimitedPayments')">
            <UCheckbox
              v-model="cashRegisterForm.data.limit_payments"
              :label="t('cash_register.limited_payments')"
            />

            <UAlert
              v-show="cashRegisterForm.data.limit_payments"
              color="warning"
              variant="soft"
              icon="i-lucide-triangle-alert"
              class="my-4"
              :description="t('cash_register.limited_payments_description')"
            />
          </template>
        </div>

        <div v-else class="flex flex-col gap-4">
          <UFormField :label="t('general.name')" :error="terminalForm.$errors.name?.[0] || undefined">
            <UInput
              v-model="terminalForm.data.name"
              class="w-full"
              @input="terminalForm.clearErrors('name')"
              @blur="terminalForm.validate($vTerminalForm, 'name')"
            />
          </UFormField>

          <UFormField
            v-if="selectedType?.type === TerminalType.orderingDevice"
            :label="t('general.cash_register')"
            :error="terminalForm.$errors.cash_register_id?.[0] || undefined"
          >
            <USelect
              v-model="terminalForm.data.cash_register_id"
              class="w-full"
              :items="cashRegisterItems"
            />
          </UFormField>
        </div>
      </div>
    </div>

    <template #footer>
      <UButton size="lg"
        v-if="selectedType"
        color="primary"
        variant="solid"
        :disabled="!enableRegister"
        @click="register"
      >
        <span v-if="selectedType.type === TerminalType.cashRegister">{{ t('terminals.cash_register_register') }}</span>
        <span v-else-if="selectedType.type === TerminalType.orderingDevice">{{ t('terminals.ordering_device_register') }}</span>
        <span v-else>{{ t('terminals.kitchen_display_system_register') }}</span>
      </UButton>
    </template>
  </CodSheet>
</template>

<script setup lang="ts">
import { ComputedRef, Ref, computed, inject, ref, watch } from 'vue';
import { CashRegister, Terminal, TerminalType, I18n, Device, ICashRegister, ITerminal, ICashRegisterRequest, ITerminalRegisterRequest, apiService, UserService, TerminalService } from 'app-core';
import { useToast } from '@/composables/useToast';
import { useI18n } from 'vue-i18n';
import { required, requiredIf } from '@vuelidate/validators'
import { useAppStore } from '@/stores/useAppStore';
import { useRouter } from 'vue-router';
import { Emitter } from '@/utilities/emitter';
import { ICodConfirmConfig } from 'app-components';
import useVuelidate from '@vuelidate/core';
import Form from '@/forms/Form';
import { CodSheet } from 'app-components';

interface ITerminalTypeOption {
  type: TerminalType
  enabled: boolean
  error: string
  icon: string
}

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: false
  }
})

const { t } = useI18n()
const { toast } = useToast()
const store = useAppStore()
const router = useRouter()
const emit = defineEmits(['update:modelValue'])
const emitter = inject('emitter') as Emitter
const codConfirm = inject('codConfirm') as (config: ICodConfirmConfig) => void

const isOpen = computed({
  get: () => props.modelValue,
  set: (v: boolean) => emit('update:modelValue', v)
})

const device = ref(Device.get())

const cashRegisters = ref(CashRegister.all())
const cashRegisterItems = computed(() =>
  cashRegisters.value.map(cashRegister => ({ label: cashRegister.getFullName(), value: cashRegister.id })))

const enableRegister = computed(() => {
  return UserService.allows('allow_cash_register')
    && terminalTypes.value.length > 0
    && !terminalForm.value.$busy
    && !cashRegisterForm.value.$busy
    && !terminalTypes.value.some(t => t.type === selectedType.value?.type && !!t.error)
})

const terminalTypes: ComputedRef<ITerminalTypeOption[]> = computed(() => {
  if(!store.tenant) return []

  const isMobile = device.value?.platform === 'android' || device.value?.platform === 'ios'
  const isWeb = device.value?.platform === 'web'

  let cashRegistersError = ''
  const cashRegistersLimit = store.tenant.status !== 'permanently_active'
    ? (store.tenant.active_tenant_contracts?.reduce((acc, cur) => (acc += cur.cash_registers_limit), 0) || 0)
    : 10000
  const cashRegistersCurrent = CashRegister.all().length
  const cashRegistersLimitReached = cashRegistersLimit <= cashRegistersCurrent

  if(cashRegistersLimitReached) cashRegistersError = 'limit_reached'
  else if(isWeb && !I18n.c('cashRegister.allowOnWebClient')) cashRegistersError = 'native_needed'

  let orderingDevicesError = ''
  const orderingDevicesLimit = store.tenant.venue?.settings?.ordering_devices_limit || 0
  const orderingDevicesCurrent = Terminal.all().filter(t => t.isOrderingDevice()).length
  const orderingDevicesLimitReached = orderingDevicesLimit <= orderingDevicesCurrent

  if(!store.modules.ordering_devices) orderingDevicesError = 'subscription_needed'
  else if(orderingDevicesLimitReached) orderingDevicesError = 'limit_reached'
  else if(!isMobile) orderingDevicesError = 'mobile_needed'

  let kdsError = ''
  const kdsLimit = 1
  const kdsCurrent = Terminal.all().filter(t => t.isKitchenDisplaySystem()).length
  const kdsLimitReached = kdsLimit <= kdsCurrent

  if(!store.modules.kds) kdsError = 'subscription_needed'
  else if(kdsLimitReached) kdsError = 'limit_reached'

  return [
    { type: TerminalType.cashRegister, icon: 'i-lucide-inbox', enabled: true, error: cashRegistersError },
    { type: TerminalType.orderingDevice, icon: 'i-lucide-smartphone', enabled: !store.isRetail && cashRegistersCurrent >= 1, error: orderingDevicesError },
    { type: TerminalType.kitchenDisplaySystem, icon: 'i-lucide-chef-hat', enabled: !store.isRetail && cashRegistersCurrent >= 1, error: kdsError }
  ]
})

const selectedType: Ref<ITerminalTypeOption | undefined> = ref(terminalTypes.value?.[0])
const showPassword = ref(false)

watch(selectedType, (val, oldVal) => {
  cashRegisterForm.value.resetData()
  cashRegisterForm.value.clearErrors()

  terminalForm.value.resetData()
  terminalForm.value.clearErrors()

  const defaultTerminalData = getTerminalDefaultData()
  terminalForm.value.data.name = defaultTerminalData.name
  terminalForm.value.data.key = defaultTerminalData.key
  terminalForm.value.data.type = defaultTerminalData.type
  terminalForm.value.data.cash_register_id = defaultTerminalData.cashRegisterId
})

function onModalPresent() {
  cashRegisters.value = CashRegister.all()
}

const handleDismiss = () => {
  emit('update:modelValue', false)
}

const createCashRegisterForm = () => {
  return new Form<ICashRegisterRequest>({
    name: t('terminals.cash_register') + ' ' + (CashRegister.all().length + 1),
    activation_code: undefined,
    reseller_hash: undefined,
    limit_payments: false,
    uuid: device.value?.uuid,
    platform: device.value?.platform,
    manufacturer: device.value?.manufacturer,
    model: device.value?.model
  })
}

const createTerminalForm = () => {
  const defaultTerminalData = getTerminalDefaultData()

  return new Form<ITerminalRegisterRequest>({
    name: defaultTerminalData.name,
    key: defaultTerminalData.key,
    type: defaultTerminalData.type,
    cash_register_id: defaultTerminalData.cashRegisterId,
    uuid: device.value?.uuid,
    platform: device.value?.platform,
    manufacturer: device.value?.manufacturer,
    model: device.value?.model
  })
}

const cashRegisterForm = ref(createCashRegisterForm())
const terminalForm = ref(createTerminalForm())

const $vCashRegisterForm = useVuelidate({
  name: { required: required },
  activation_code: { requiredIf: requiredIf(I18n.c('order.registerMethod') === 'legacy') },
  reseller_hash: { requiredIf: requiredIf(I18n.c('cashRegister.requireResellerHash')) },
}, cashRegisterForm.value.data)

const $vTerminalForm = useVuelidate({
  name: { required: required },
  cash_register_id: { requiredIf: requiredIf(selectedType.value?.type === TerminalType.orderingDevice) },
}, terminalForm.value.data)

async function register() {
  const type = selectedType.value?.type

  if(type === TerminalType.cashRegister && !cashRegisterForm.value.validate($vCashRegisterForm.value)) return
  if(type !== TerminalType.cashRegister && !terminalForm.value.validate($vTerminalForm.value)) return

  codConfirm({
    title: getTerminalTypeLabel(type),
    message: t('terminals.registration_warning', { value: getTerminalTypeLabel(type) }),
    buttonTrueText: t('general.confirm'),
    buttonCancelText: t('general.cancel'),
    yesHandler: async () => {
      if(type === TerminalType.cashRegister) registerCashRegister()
      else registerTerminal()
    }
  })
}

async function registerCashRegister() {
  try {
    const response = await apiService.post<ICashRegister>('/cash-registers', cashRegisterForm.value)
    handleDismiss()

    if(response.data.terminal) {
      await TerminalService.setTerminal(response.data.terminal, response.data)
      toast(t('terminals.registration_success'), 'success')
      emitter.emit('ordersRefresh')
    } else {
      toast(t('terminals.registration_failed'), 'error')
    }
  } catch (error: any) {
    const errorCode = error.response?.data?.errors?.[0].code || null
    if(errorCode) toast(t(`cash_register.error.${errorCode}`), 'error')
    else toast(t('terminals.registration_failed'), 'error')
  }
}

async function registerTerminal() {
  try {
    const response = await apiService.post<{ terminal: ITerminal, cashRegister: ICashRegister | null }>('/terminals', terminalForm.value)
    handleDismiss()

    await TerminalService.setTerminal(response.data.terminal, response.data.cashRegister || undefined)

    if(selectedType.value?.type === TerminalType.kitchenDisplaySystem) {
      router.push({ name: 'kitchen' })
      return
    }

    if(selectedType.value?.type === TerminalType.orderingDevice) {
      emitter.emit('ordersRefresh')
      toast(t('terminals.registration_success'), 'success')

      setTimeout(() => location.reload(), 1500)
    }
  } catch (error) {
    console.log(error)

    if(terminalForm.value.$errors.name?.length || terminalForm.value.$errors.cash_register_id?.length) return

    if(terminalForm.value.$errors.uuid?.length) {
      terminalForm.value.setErrors({ name: [t('terminals.already_registered')] })
    }

    toast(t(`terminals.registration_failed`), 'error')
  }
}

function getTerminalTypeLabel(type: TerminalType | undefined) {
  if(!type) return ''

  if(type === TerminalType.cashRegister) return t('terminals.cash_register')
  else if(type === TerminalType.orderingDevice) return t('terminals.ordering_device')
  return t('terminals.kitchen_display_system')
}

function getTerminalDefaultData() {
  const type = selectedType.value?.type || TerminalType.cashRegister

  const terminals = {
    [TerminalType.cashRegister]: Terminal.all().filter(t => t.isCashRegister()),
    [TerminalType.orderingDevice]: Terminal.all().filter(t => t.isOrderingDevice()),
    [TerminalType.kitchenDisplaySystem]: Terminal.all().filter(t => t.isKitchenDisplaySystem()),
  }

  return {
    name: getTerminalTypeLabel(type) + ' ' + (terminals[type].length + 1),
    key: type + (Math.max(...Terminal.all().map(t => t.id as number)) + 1),
    type: type,
    cashRegisterId: type === TerminalType.orderingDevice ? (cashRegisters.value[0]?.id || undefined) : undefined
  }
}

// immediate: lazily mounted with the model already open (see MainLayout).
watch(() => props.modelValue, (val) => {
  if(val) onModalPresent()
}, { immediate: true })
</script>
